import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaRobot, FaSpinner, FaBoxes, FaPaperPlane as FaSend } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import ContainerQuoteWizard from '../components/ContainerQuoteWizard';
import { rateCards, quotes, clientQuotes } from '../api';

// Client-facing simplified container estimator.
// Reached from the client's New Job page - NOT a nav tab.
// Uses the SAME Benecke rate-card engine + wizard (AI chat included).
// Estimate shows the REAL price including the 5% margin.
// "Request Official Quote" creates a quote with status client_request
// so it lands in the admin Quotes list as a Price & Send item.
function ClientContainerEstimatorPage({ onLogout }) {
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [clientId, setClientId] = useState(null);
  const [calcQuote, setCalcQuote] = useState(null);
  const [liveWizard, setLiveWizard] = useState({});
  const [requesting, setRequesting] = useState(false);

  const [chatMessages, setChatMessages] = useState([
    { role: 'ai', text: "Hi! Tell me what containers you need moved - e.g. \"2 x 6m containers from Benoni to Durban, they are empty\". I'll fill the form for you. I never set the price - the rate engine calculates a fair estimate." }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    // Resolve the logged-in client's record (same pattern as the My Quotes page)
    (async () => {
      try {
        const res = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
        const data = await res.json();
        if (data.success && data.clients && data.clients.length > 0) {
          setClientId(data.clients[0].id);
        }
      } catch (e) {
        console.error('Resolve client id error:', e);
      }
    })();
  }, []);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const history = chatMessages.slice(-10);
      const res = await rateCards.assist(text, liveWizard, history);
      if (res.success) {
        const aiReplies = [];
        if (res.reply) aiReplies.push({ role: 'ai', text: res.reply });
        if (res.questions && res.questions.length > 0) {
          aiReplies.push({ role: 'ai', text: res.questions.join(' ') });
        }
        if (res.fields && Object.keys(res.fields).length > 0) {
          window.dispatchEvent(new CustomEvent('container-wizard-fill', { detail: res.fields }));
        }
        setChatMessages(prev => [...prev, ...aiReplies]);
      } else {
        setChatMessages(prev => [...prev, { role: 'ai', text: `Error: ${res.error || 'Assistant error'}` }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'ai', text: `Error: ${err.message || 'Assistant unavailable. Manual wizard still works.'}` }]);
    } finally {
      setChatLoading(false);
    }
  };

  const buildDescription = (q) => {
    if (!q) return null;
    const req = q.request || {};
    const count = req.container_count || 1;
    const size = req.container_size || '6m';
    const type = String(req.container_type || 'dry').replace(/_/g, ' ');
    const from = req.pickup_town ? String(req.pickup_town).split(',')[0].trim() : '';
    const to = req.dropoff_town ? String(req.dropoff_town).split(',')[0].trim() : '';
    const load = req.site_loading ? 'Client loads and offloads' : 'Includes loading and offloading';
    const extras = [];
    if (req.weekend) extras.push('weekend/overtime');
    if (req.cross_border) extras.push('cross-border');
    if (req.mine_site) extras.push('mine safety');
    if (req.extra_hours) extras.push('extra hours');
    if (req.standing_hours) extras.push('standing time');
    if (req.extra_stops) extras.push('extra stops');
    const extrasTxt = extras.length ? ` Extras: ${extras.join(', ')}.` : ' No extras.';
    return `${count} x ${size} ${type} container${count > 1 ? 's' : ''} transported from ${from} to ${to}. ${load}.${extrasTxt}`;
  };

  const handleRequestOfficialQuote = async () => {
    if (!calcQuote) {
      toast.error('Calculate an estimate first (bottom of the wizard)');
      return;
    }
    if (!clientId) {
      toast.error('Could not identify your client account. Contact support.');
      return;
    }
    setRequesting(true);
    try {
      const payload = {
        quote_type: 'container',
        customer_id: clientId,
        status: 'client_request',
        customer_name: user.company_name || user.full_name || null,
        description: buildDescription(calcQuote),
        origin: calcQuote?.request?.pickup_town || null,
        destination: calcQuote?.request?.dropoff_town || null,
        distance_km: calcQuote?.town?.km_from_edenvale || null,
        truck_type: calcQuote?.truck?.code || null,
        rate_card_details: calcQuote?.request || null,
        rate_card_breakdown: calcQuote?.line_items || null,
        fuel_index_at_quote: calcQuote?.fuel?.index || null,
        rate_card_version: 'benecke-2021-fuel-indexed',
        margin_percentage: calcQuote?.margin_percentage != null ? calcQuote.margin_percentage : null,
        margin_amount: calcQuote?.totals?.margin_amount != null ? calcQuote.totals.margin_amount : null,
        items: (calcQuote.line_items || []).map((li) => {
          const qty = typeof li.quantity === 'number' && li.quantity ? li.quantity : 1;
          let costPerUnit;
          if (li.type === 'base' && typeof li.fuel_indexed_unit_price === 'number') {
            costPerUnit = parseFloat(li.fuel_indexed_unit_price);
          } else {
            const lineTotal = (typeof li.total === 'number' && li.total != null)
              ? parseFloat(li.total)
              : (parseFloat(li.unit_price) || 0) * qty;
            costPerUnit = lineTotal / qty;
          }
          const marginPct = parseFloat(calcQuote.margin_percentage) || 0;
          const roundedCost = Math.round(costPerUnit * 100) / 100;
          const unitPrice = Math.round(roundedCost * (1 + marginPct) * 100) / 100;
          return {
            description: li.description,
            quantity: qty,
            cost_price: roundedCost,
            unit_price: unitPrice,
            total: Math.round(qty * unitPrice * 100) / 100,
            margin_percentage: Math.round(marginPct * 100)
          };
        }),
        route: {
          pickup_address: calcQuote?.request?.pickup_town || '',
          delivery_address: calcQuote?.request?.dropoff_town || '',
          distance_km: calcQuote?.town?.km_from_edenvale || null
        },
        created_by: user.id || null
      };

      let res;
      try {
        res = await clientQuotes.request(payload);
      } catch (e) {
        console.error('Client quote request error:', e);
        res = { success: false, error: e.message || 'Request failed' };
      }

      if (res && res.success) {
        toast.success('Estimate submitted! Our team will confirm your official quote shortly.');
        navigate('/client-quotes');
      } else {
        toast.error(res?.error || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Request official quote error:', err);
      toast.error(err.message || 'Failed to submit request');
    } finally {
      setRequesting(false);
    }
  };

  return (
    <ClientLayout activeTab="newjob" onLogout={onLogout} user={user}>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/client-new-job')} className="p-2 rounded-lg hover:bg-gray-100 text-navy">
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="text-xl font-bold text-navy flex items-center gap-2">
              <FaBoxes className="text-yellow-500" /> Container Estimator
            </h1>
            <p className="text-sm text-gray-500">Get an instant estimate - then request an official quote.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 flex flex-col">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaRobot className="text-yellow-500" /> AI Assistant
            </h2>
            <div className="flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1 mb-4 bg-gray-50 rounded-lg p-3">
              {chatMessages.map((m, i) => (
                <div key={i} className={`max-w-[90%] px-3 py-2 rounded-lg text-sm whitespace-pre-line ${m.role === 'user' ? 'bg-yellow-400 text-navy ml-auto font-medium' : 'bg-white border border-gray-200 text-gray-800'}`}>
                  {m.text}
                </div>
              ))}
              {chatLoading && (
                <div className="bg-white border border-gray-200 px-3 py-2 rounded-lg text-sm text-gray-500 flex items-center gap-2 max-w-[90%]">
                  <FaSpinner className="animate-spin text-yellow-500" /> Thinking...
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChat(); } }}
                placeholder="e.g. 2 containers from Benoni to Durban, empty"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={sendChat}
                disabled={chatLoading}
                className="px-4 py-2 bg-yellow-400 text-navy rounded-lg hover:bg-yellow-300 disabled:opacity-50 flex items-center gap-1 text-sm font-bold shadow"
              >
                <FaSend size={12} />
              </button>
            </div>
          </div>

          <div className="lg:col-span-3">
            <ContainerQuoteWizard onCalculated={(q) => setCalcQuote(q)} onClear={() => setCalcQuote(null)} onStateChange={(w) => setLiveWizard(w)} />

            {calcQuote && (
              <div className="mt-4 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                  <FaBoxes className="text-yellow-500" /> Your Estimate
                </h2>
                <div className="text-sm text-gray-600 mb-4">
                  {calcQuote.truck?.label} · {calcQuote.request?.container_count || 1} x {calcQuote.request?.container_size || '6m'} · {calcQuote.request?.is_empty ? 'Empty' : 'Loaded'} · {parseFloat(calcQuote?.town?.km_from_edenvale || 0).toFixed(0)} km
                </div>
                <div className="text-3xl font-bold text-green-700 mb-4">
                  R {(parseFloat(calcQuote?.totals?.grand_total || 0)).toFixed(2)}
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  This estimate uses the same rate card as our official quotes. Final quote may vary slightly.
                </p>
                <button
                  onClick={handleRequestOfficialQuote}
                  disabled={requesting}
                  className="w-full px-6 py-3 bg-navy text-white rounded-lg text-sm font-bold hover:bg-yellow-400 hover:text-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {requesting ? <><FaSpinner className="animate-spin" /> Submitting...</> : <>Request Official Quote</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}

export default ClientContainerEstimatorPage;